import React, { FormEvent, useEffect, useState } from "react";
import api from "../../../api";
import "../../../styles/Login.scss";
import ProductSizeModal from "./ProductSizeModal";
import RichTextEditor from "./RichTextEditor";

interface Range {
    id: number;
    name: string;
    active: boolean;
    display_order: number;
}

interface ProductSize {
    id: number;
    product_id: number;
    title: string;
    capacity: string;
    pack_shot: string | null;
    active: boolean;
    display_order: number;
}

interface Product {
    id: number;
    range_id: number;
    name: string;
    description: string;
    about: string | null;
    ingredients: string | null;
    color_code: string;
    active: boolean;
    display_order: number;
    sizes?: ProductSize[];
}

interface ProductModalProps {
    product?: Product | null;
    ranges: Range[];
    onClose: () => void;
    onSaved: () => void;
}

const ProductModal: React.FC<ProductModalProps> = ({
                                                       product,
                                                       ranges,
                                                       onClose,
                                                       onSaved,
                                                   }) => {
    const isEditing = !!product;

    const [productId, setProductId] = useState<number | null>(
        product?.id ?? null
    );

    const [name, setName] = useState(product?.name ?? "");
    const [rangeId, setRangeId] = useState(
        product?.range_id ? String(product.range_id) : ""
    );
    const [description, setDescription] = useState(
        product?.description ?? ""
    );

    const [about, setAbout] = useState(
        product?.about ?? ""
    );

    const [ingredients, setIngredients] = useState(
        product?.ingredients ?? ""
    );

    const [colorCode, setColorCode] = useState(
        product?.color_code ?? ""
    );
    const [active, setActive] = useState(product?.active ?? true);
    const [displayOrder, setDisplayOrder] = useState(
        product?.display_order ?? 0
    );

    const [sizes, setSizes] = useState<ProductSize[]>(
        product?.sizes ?? []
    );

    const [loading, setLoading] = useState(false);
    const [sizesLoading, setSizesLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [showSizeModal, setShowSizeModal] = useState(false);
    const [editingSize, setEditingSize] = useState<ProductSize | null>(
        null
    );

    const [deletingSizeId, setDeletingSizeId] = useState<number | null>(
        null
    );

    const [movingSizeId, setMovingSizeId] = useState<number | null>(
        null
    );

    useEffect(() => {
        if (product?.id) {
            setProductId(product.id);
            setName(product.name ?? "");
            setRangeId(
                product.range_id ? String(product.range_id) : ""
            );

            setDescription(product.description ?? "");
            setAbout(product.about ?? "");
            setIngredients(product.ingredients ?? "");
            setColorCode(product.color_code ?? "");
            setActive(product.active ?? true);
            setDisplayOrder(product.display_order ?? 0);
            fetchSizes(product.id);
        }
    }, [product]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setError("");
        setSuccess("");

        if (!name.trim()) {
            setError("Product name is required.");
            return;
        }

        if (!rangeId) {
            setError("Please select a range.");
            return;
        }

        try {
            setLoading(true);

            const payload = {
                name: name.trim(),
                range_id: Number(rangeId),
                description,
                about,
                ingredients,
                color_code: colorCode.trim(),
                active,
                display_order: Number(displayOrder),
            };

            let savedProduct: Product;

            if (isEditing && productId) {
                const response = await api.put(
                    `/admin/products/${productId}`,
                    payload
                );

                savedProduct =
                    response.data?.data ?? response.data;

                setProductId(savedProduct.id);

                setSuccess("Product saved successfully.");

                onSaved();
            } else {
                const response = await api.post(
                    "/admin/products",
                    payload
                );

                savedProduct =
                    response.data?.data ?? response.data;

                setProductId(savedProduct.id);

                setSuccess(
                    "Product created. You can now add product sizes."
                );

                onSaved();
            }
        } catch (err: any) {
            console.error(err);

            if (err.response?.data?.errors) {
                const validationErrors =
                    err.response.data.errors;

                const firstError =
                    Object.values(validationErrors)[0];

                if (Array.isArray(firstError)) {
                    setError(String(firstError[0]));
                } else {
                    setError(String(firstError));
                }
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save product."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchSizes = async (id: number) => {
        try {
            setSizesLoading(true);
            setError("");

            const response = await api.get(
                `/admin/products/${id}/sizes`
            );

            const data = response.data?.data ?? response.data ?? [];

            setSizes(
                [...data].sort(
                    (a: ProductSize, b: ProductSize) =>
                        a.display_order - b.display_order
                )
            );
        } catch (err: any) {
            console.error("Failed to load product sizes:", err);
            console.error("Response:", err.response?.data);

            setError("Failed to load product sizes.");
        } finally {
            setSizesLoading(false);
        }
    };

    const handleAddSize = () => {
        if (!productId) {
            setError(
                "Please save the product before adding product sizes."
            );
            return;
        }

        setEditingSize(null);
        setShowSizeModal(true);
    };

    const handleEditSize = (size: ProductSize) => {
        setEditingSize(size);
        setShowSizeModal(true);
    };

    const handleSizeSaved = async () => {
        setShowSizeModal(false);
        setEditingSize(null);

        if (productId) {
            await fetchSizes(productId);
        }
    };

    const handleDeleteSize = async (size: ProductSize) => {
        if (
            !window.confirm(
                `Are you sure you want to delete "${size.title}"?`
            )
        ) {
            return;
        }

        try {
            setDeletingSizeId(size.id);
            setError("");

            await api.delete(
                `/admin/product-sizes/${size.id}`
            );

            if (productId) {
                await fetchSizes(productId);
            }
        } catch (err: any) {
            console.error(err);

            setError(
                err.response?.data?.message ||
                "Failed to delete product size."
            );
        } finally {
            setDeletingSizeId(null);
        }
    };

    const handleMoveSize = async (
        size: ProductSize,
        direction: "up" | "down"
    ) => {
        try {
            setMovingSizeId(size.id);
            setError("");

            await api.post(
                `/admin/product-sizes/${size.id}/move`,
                {
                    direction,
                }
            );

            if (productId) {
                await fetchSizes(productId);
            }
        } catch (err: any) {
            console.error(err);

            setError(
                err.response?.data?.message ||
                "Failed to reorder product size."
            );
        } finally {
            setMovingSizeId(null);
        }
    };

    const getPackShotUrl = (path: string | null) => {
        if (!path) {
            return "";
        }

        if (
            path.startsWith("http://") ||
            path.startsWith("https://")
        ) {
            return path;
        }

        return `${window.location.origin}/${path}`;
    };

    return (
        <>
            <div className="admin-modal-overlay" onClick={onClose}>
                <div className="admin-modal admin-product-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-modal-header">
                        <h2>
                            {isEditing
                                ? "Edit Product"
                                : "Add Product"}
                        </h2>

                        <button
                            type="button"
                            className="admin-modal-close"
                            onClick={onClose}
                        >
                            ×
                        </button>
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="admin-modal-content"
                    >
                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="product-name">
                                Product Name
                            </label>

                            <input
                                id="product-name"
                                type="text"
                                value={name}
                                onChange={(e) =>
                                    setName(e.target.value)
                                }
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="product-range">
                                Range
                            </label>

                            <select
                                id="product-range"
                                value={rangeId}
                                onChange={(e) =>
                                    setRangeId(e.target.value)
                                }
                                required
                            >
                                <option value="">
                                    Select a range
                                </option>

                                {ranges
                                    .filter(
                                        (range) => range.active
                                    )
                                    .sort(
                                        (a, b) =>
                                            a.display_order -
                                            b.display_order
                                    )
                                    .map((range) => (
                                        <option
                                            key={range.id}
                                            value={range.id}
                                        >
                                            {range.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="product-description">
                                Description
                            </label>

                            <textarea
                                id="product-description"
                                value={description}
                                onChange={(e) =>
                                    setDescription(e.target.value)
                                }
                                rows={6}
                            />
                        </div>

                        <div className="form-group">
                            <label>About</label>

                            <RichTextEditor
                                value={about}
                                onChange={setAbout}
                                placeholder="Enter information about this product..."
                            />
                        </div>

                        <div className="form-group">
                            <label>Ingredients</label>

                            <RichTextEditor
                                value={ingredients}
                                onChange={setIngredients}
                                placeholder="Enter the ingredients..."
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="product-color">
                                Colour Code
                            </label>

                            <div className="admin-color-input">
                                {/*<input*/}
                                {/*    id="product-color"*/}
                                {/*    type="color"*/}
                                {/*    value={colorCode || "#000000"}*/}
                                {/*    onChange={(e) => setColorCode(e.target.value)}*/}
                                {/*/>*/}

                                <span
                                    className="admin-color-preview"
                                    style={{
                                        backgroundColor: colorCode || "transparent",
                                    }}
                                />
                                <input
                                    type="text"
                                    value={colorCode}
                                    onChange={(e) => setColorCode(e.target.value)}
                                    placeholder="#000000"
                                />


                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="product-display-order">
                                Display Order
                            </label>

                            <input
                                id="product-display-order"
                                type="number"
                                min="0"
                                value={displayOrder}
                                onChange={(e) =>
                                    setDisplayOrder(
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </div>

                        <div className="form-group">
                            <label className="admin-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={active}
                                    onChange={(e) =>
                                        setActive(
                                            e.target.checked
                                        )
                                    }
                                />

                                <span>Active</span>
                            </label>
                        </div>

                        <div className="admin-content-section">
                            <div className="admin-page-header">
                                <div>
                                    <h3>Product Sizes</h3>

                                    <p>
                                        Add the sizes available for
                                        this product.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleAddSize}
                                    disabled={!productId}
                                >
                                    Add Size
                                </button>
                            </div>

                            {!productId ? (
                                <div className="admin-empty-state">
                                    Save the product first to add
                                    product sizes.
                                </div>
                            ) : sizesLoading ? (
                                <div className="admin-loading">
                                    Loading sizes...
                                </div>
                            ) : sizes.length === 0 ? (
                                <div className="admin-empty-state">
                                    No product sizes have been added
                                    yet.
                                </div>
                            ) : (
                                <div className="admin-users-table">
                                    <table>
                                        <thead>
                                        <tr>
                                            <th>Order</th>
                                            <th>Title</th>
                                            <th>Capacity</th>
                                            <th>Active</th>
                                            <th>Actions</th>
                                        </tr>
                                        </thead>

                                        <tbody>
                                        {sizes.map(
                                            (
                                                size,
                                                index
                                            ) => (
                                                <tr key={size.id}>
                                                    <td>
                                                        <div className="admin-user-actions">
                                                            <button
                                                                type="button"
                                                                className="admin-table-button"
                                                                disabled={
                                                                    index === 0 ||
                                                                    movingSizeId === size.id
                                                                }
                                                                onClick={() =>
                                                                    handleMoveSize(size, "up")
                                                                }
                                                                title="Move up"
                                                            >
                                                                ↑
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="admin-table-button"
                                                                disabled={
                                                                    index === sizes.length - 1 ||
                                                                    movingSizeId === size.id
                                                                }
                                                                onClick={() =>
                                                                    handleMoveSize(size, "down")
                                                                }
                                                                title="Move down"
                                                            >
                                                                ↓
                                                            </button>
                                                        </div>
                                                    </td>

                                                    <td>
                                                        {size.title}
                                                    </td>

                                                    <td>
                                                        {size.capacity}
                                                    </td>

                                                    <td>
                                                        {size.active ? "Yes" : "No"}
                                                    </td>

                                                    <td>
                                                        <div className="admin-user-actions">
                                                            <button
                                                                type="button"
                                                                className="admin-table-button"
                                                                onClick={() =>
                                                                    handleEditSize(size)
                                                                }
                                                            >
                                                                Edit
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="admin-table-button admin-table-button-danger"
                                                                disabled={deletingSizeId === size.id}
                                                                onClick={() =>
                                                                    handleDeleteSize(size)
                                                                }
                                                            >
                                                                {deletingSizeId === size.id
                                                                    ? "Deleting..."
                                                                    : "Delete"}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="admin-modal-actions">
                            <button
                                type="button"
                                className="admin-table-button"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="admin-primary-button"
                                disabled={loading}
                                onClick={() => {
                                    handleSubmit({ preventDefault: () => {} } as FormEvent);
                                }}
                            >
                                {loading ? "Saving..." : "Save Changes"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {showSizeModal && productId && (
                <ProductSizeModal
                    productId={productId}
                    size={editingSize}
                    onClose={() => {
                        setShowSizeModal(false);
                        setEditingSize(null);
                    }}
                    onSaved={handleSizeSaved}
                />
            )}
        </>
    );
};

export default ProductModal;
